import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "src/components/ui/alert";
import { Button } from "src/components/ui/button";
import { Card, CardContent, CardHeader } from "src/components/ui/card";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  saveCalculatorData,
} from "src/utils/functions";

import { useIsVerified } from "src/components/email-verification";
import { sanitizeBrlValue } from "../simulador-poupanca-selic";
import { SimpleInterestResponse } from "./components/JurosSimplesResponse";

interface SimpleInterestResults {
  monthlyInterest: number[];
  monthlyAccumulated: number[];
  totalInterest: number;
  totalAmount: number;
  principal: number;
  months: number;
}

const calculatorKey = "juros-simples";

export const JurosSimples = () => {
  const [principal, setPrincipal] = useState<string>("");
  const [rate, setRate] = useState<string>("");
  const [period, setPeriod] = useState<string>("");
  const [periodType, setPeriodType] = useState<string>("ano(s)");
  const [rateType, setRateType] = useState<string>("anual");
  const [error, setError] = useState<string>("");
  const [results, setResults] = useState<SimpleInterestResults | null>(null);
  const isVerified = useIsVerified();

  function clear() {
    setPrincipal("");
    setRate("");
    setPeriod("");
    setPeriodType("ano(s)");
    setRateType("anual");
    setResults(null);
    setError("");
  }

  const calculateSimpleInterest = () => {
    setError("");

    if (!principal || !rate || !period) {
      setError("Por favor, preencha todos os campos");
      return;
    }

    const principalAmount = Number.parseFloat(
      principal.replace(/[^0-9,-]+/g, "").replace(",", ".")
    );

    const initialRate = Number.parseFloat(rate) / 100;
    const timePeriod = Number.parseFloat(period);

    if (principalAmount <= 0 || initialRate <= 0 || timePeriod <= 0) {
      setError("Todos os valores devem ser maiores que zero");
      return;
    }
    const months = periodType === "ano(s)" ? timePeriod * 12 : timePeriod;

    const monthlyRate = rateType === "anual" ? initialRate / 12 : initialRate;

    const monthlyInterest: number[] = [];
    const monthlyAccumulated: number[] = [];

    for (let i = 0; i <= months; i++) {
      const interest = (principalAmount * monthlyRate * i * 1000) / 1000;
      monthlyInterest.push(interest);
      monthlyAccumulated.push(principalAmount + interest);
    }

    const totalInterest = monthlyInterest[monthlyInterest.length - 1];
    const totalAmount = principalAmount + totalInterest;

    setResults({
      monthlyInterest: monthlyInterest.map((val) => (val * 1000) / 1000),
      monthlyAccumulated: monthlyAccumulated.map((val) => (val * 1000) / 1000),
      totalInterest: (totalInterest * 1000) / 1000,
      totalAmount: (totalAmount * 1000) / 1000,
      principal: (principalAmount * 1000) / 1000,
      months,
    });
  };

  function handleSave() {
    saveCalculatorData(calculatorKey, {
      principal,
      rate,
      period,
      periodType,
      rateType,
    });
  }

  useEffect(() => {
    const savedData = loadSavedCalculatorData(calculatorKey);
    if (!savedData) return;
    setPrincipal((savedData.principal as string) || "");
    setRate((savedData.rate as string) || "");
    setPeriod((savedData.period as string) || "");
    setPeriodType((savedData.periodType as string) || "ano(s)");
    setRateType((savedData.rateType as string) || "anual");
  }, []);

  return (
    <div className="w-full max-w-4xl mx-auto p-4">
      <Card>
        <CardHeader>
          <h1 className="text-primary text-xl font-bold">
            Simulador de Juros Simples
          </h1>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
              checkCalculatorUsageLimitAndRedirect(calculateSimpleInterest);
            }}
            className="grid gap-4"
          >
            <div className="grid md:grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="principal">Valor inicial</Label>
                <div className="relative">
                  <Input
                    id="principal"
                    type="text"
                    placeholder="1.000,00"
                    className="pl-10"
                    value={principal}
                    onChange={(e) => setPrincipal(sanitizeBrlValue(e))}
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="rate">Taxa de juros</Label>
                <div className="flex gap-2">
                  <Input
                    id="rate"
                    type="number"
                    placeholder="6,25"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                  />
                  <Select value={rateType} onValueChange={setRateType}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Período" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="anual">anual</SelectItem>
                      <SelectItem value="mensal">mensal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="period">Período</Label>
                <div className="flex gap-2">
                  <Input
                    id="period"
                    type="number"
                    placeholder="12"
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                  />
                  <Select value={periodType} onValueChange={setPeriodType}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ano(s)">ano(s)</SelectItem>
                      <SelectItem value="mes(es)">mes(es)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <Button
                type="submit"
                variant={"default"}
                disabled={!isVerified}
                className="sm:w-[150px]"
              >
                Calcular
              </Button>
              <Button onClick={clear} variant={"ghost"}>
                Limpar
              </Button>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </form>

          {results && <SimpleInterestResponse results={results} />}
        </CardContent>
      </Card>
    </div>
  );
};
